import React from "react";
import { Lead, LeadStatus, LeadOutcome } from "../types/LeadTypes";
import LeadTableHeaderWrapper from "./LeadTableHeaderWrapper";
import LeadTableContent from "./LeadTableContent";
import LeadListPagination from "../pagination/LeadListPagination";
import { useLeadListState } from "./hooks/useLeadListState";
import LeadModals from "../modals";
import { formatTranscriptForDisplay } from "../utils/transcriptUtils";

interface DesktopLeadListProps {
  leads: Lead[];
  onStatusChange: (id: string, status: LeadStatus) => void;
  onOutcomeChange: (id: string, outcome: LeadOutcome | null) => void;
  onValueChange: (id: string, value: string) => void;
  onNameChange: (id: string, name: string) => void;
}

const DesktopLeadList: React.FC<DesktopLeadListProps> = ({
  leads,
  onStatusChange,
  onOutcomeChange,
  onValueChange,
  onNameChange,
}) => {
  const columnStyles = {
    toggle: "w-[30px] min-w-[30px] p-2 text-center",
    name: "w-[120px] min-w-[120px]",
    phone: "w-[100px] min-w-[100px]",
    status: "w-[110px] min-w-[110px]",
    outcome: "w-[180px] min-w-[180px]",
    value: "w-[80px] min-w-[80px]",
    date: "w-[120px] min-w-[120px]",
  };

  const {
    paginatedLeads,
    openLeads,
    editingName,
    transcriptModalOpen,
    activeLeadId,
    currentPage,
    pageSize,
    totalPages,
    toggleLead,
    openNameModal,
    closeNameModal,
    handleNameChange,
    openTranscriptModal,
    closeTranscriptModal,
    handleStatusChange,
    handleOutcomeChange,
    handlePageChange,
    handlePageSizeChange,
  } = useLeadListState({
    leads,
    onStatusChange,
    onOutcomeChange,
    onValueChange,
    onNameChange,
  });

  return (
    <div className="relative w-full flex flex-col rounded-lg overflow-hidden">
      <div className="border rounded-tl-lg rounded-tr-lg overflow-hidden">
        <LeadTableHeaderWrapper columnStyles={columnStyles} />

        <LeadTableContent
          leads={paginatedLeads}
          openLeads={openLeads}
          columnStyles={columnStyles}
          onToggle={toggleLead}
          onOpenNameModal={openNameModal}
          onStatusChange={handleStatusChange}
          onOutcomeChange={handleOutcomeChange}
          onValueChange={onValueChange}
          onOpenTranscriptModal={openTranscriptModal}
        />
      </div>

      <div className="sticky bottom-0 border-t-0 border-l border-r border-b border-gray-200 p-4 bg-white rounded-bl-lg rounded-br-lg">
        <LeadListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalItems={leads.length}
        />
      </div>

      <LeadModals
        editingName={editingName}
        transcriptModalOpen={transcriptModalOpen}
        activeLeadId={activeLeadId}
        initialName={
          editingName
            ? leads.find((lead) => lead.id === editingName)?.name || ""
            : ""
        }
        transcript={
          activeLeadId
            ? formatTranscriptForDisplay(
                leads.find((lead) => lead.id === activeLeadId) || leads[0]
              )
            : []
        }
        onCloseNameModal={closeNameModal}
        onCloseTranscriptModal={closeTranscriptModal}
        onSaveName={handleNameChange}
      />
    </div>
  );
};

export default DesktopLeadList;
